import { useState, useEffect } from 'react';
import { useJwt, useAuthHeaders } from '@campus/core';
import { API_BASE } from '../../lib/apiBase.js';
import { Button, Input, Select } from '../ds/index.js';

interface InviteLink {
  id: string;
  zoneSlug: string | null;
  maxUses: number;
  usedCount: number;
  expiresAt: string;
  createdAt: string;
  url?: string;
}

function getTenantIdFromJwt(jwt: string | null): string {
  if (!jwt) return '';
  try {
    const payload = JSON.parse(atob(jwt.split('.')[1]?.replace(/-/g, '+').replace(/_/g, '/') ?? ''));
    return (payload.tid as string) ?? '';
  } catch { return ''; }
}

const linkUrl = (link: InviteLink) => `${window.location.origin}/guest?invite=${link.id}`;
const shortUrl = (link: InviteLink) => `${window.location.host}/guest?invite=${link.id.slice(0, 4)}…${link.id.slice(-3)}`;
const formatDate = (iso: string) =>
  new Date(iso).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/**
 * AdminGuestLinks — Admin › Membres › Liens invités : accès sans compte
 * (guest JWT, 8 h max), limité à une zone ou à tout l'espace.
 * Ancien « Inviter des invités » du menu Admin en jeu.
 */
export function AdminGuestLinks() {
  const jwt = useJwt();
  const tenantId = getTenantIdFromJwt(jwt);
  const authHeaders = useAuthHeaders(tenantId);

  const [links, setLinks] = useState<InviteLink[]>([]);
  const [zones, setZones] = useState<Array<{ slug: string; displayName: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [zoneSlug, setZoneSlug] = useState('');
  const [maxUses, setMaxUses] = useState(50);
  const [ttlHours, setTtlHours] = useState(72);

  useEffect(() => {
    if (!jwt || !tenantId) { setLoading(false); return; }
    let unmounted = false;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    fetch(`${API_BASE}/auth/invites`, { headers: authHeaders, signal: controller.signal })
      .then((res) => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.json(); })
      .then((data) => { if (Array.isArray(data)) setLinks(data as InviteLink[]); })
      .catch((err) => {
        if (unmounted) return;
        setError((err as Error).name === 'AbortError' ? 'Le serveur ne répond pas. Réessaie dans un instant.' : 'Impossible de charger les liens.');
      })
      .finally(() => { clearTimeout(timeout); if (!unmounted) setLoading(false); });
    fetch(`${API_BASE}/world/zones`, { headers: authHeaders })
      .then((res) => res.json())
      .then((data: Array<{ slug: string; displayName: string }>) => setZones(data.map((z) => ({ slug: z.slug, displayName: z.displayName }))))
      .catch(() => setZones([]));
    return () => { unmounted = true; controller.abort(); clearTimeout(timeout); };
  }, [jwt, tenantId]);

  const zoneName = (slug: string | null) => (slug ? zones.find((z) => z.slug === slug)?.displayName ?? slug : "Tout l'espace");

  async function handleCreate() {
    if (!jwt) return;
    setCreating(true);
    setError(null);
    try {
      const body: Record<string, unknown> = { maxUses, ttlHours };
      if (zoneSlug) body.zoneSlug = zoneSlug;
      const res = await fetch(`${API_BASE}/auth/invite`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json() as { id: string; url: string };
      if (data?.id) {
        const link: InviteLink = {
          id: data.id, zoneSlug: zoneSlug || null, maxUses, usedCount: 0,
          expiresAt: new Date(Date.now() + ttlHours * 3600_000).toISOString(),
          createdAt: new Date().toISOString(), url: data.url,
        };
        setLinks((prev) => [link, ...prev]);
        copy(link);
      }
    } catch {
      setError("Le lien n'a pas pu être créé. Réessaie.");
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(link: InviteLink) {
    if (!jwt || !window.confirm('Désactiver ce lien ? Les personnes qui ne l’ont pas encore utilisé ne pourront plus entrer.')) return;
    try {
      const res = await fetch(`${API_BASE}/auth/invite/${link.id}`, { method: 'DELETE', headers: authHeaders });
      if (res.ok) setLinks((prev) => prev.filter((l) => l.id !== link.id));
    } catch { /* ignore */ }
  }

  function copy(link: InviteLink) {
    navigator.clipboard.writeText(linkUrl(link)).catch(() => { /* HTTP : copie manuelle */ });
    setCopied(link.id);
    setTimeout(() => setCopied((c) => (c === link.id ? null : c)), 2000);
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 3fr)', gap: 24, alignItems: 'start' }}>
      <section className="pl-panel" aria-label="Nouveau lien invité" style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <h2 className="pl-card-title">Nouveau lien invité</h2>
          <p className="pl-page-sub">Accès sans compte, pour une visite ou une session ponctuelle. Chaque invité reste connecté 8 heures maximum.</p>
        </div>
        <Select
          label="Accès"
          value={zoneSlug}
          onChange={(e) => setZoneSlug(e.target.value)}
          options={[{ value: '', label: "Tout l'espace" }, ...zones.map((z) => ({ value: z.slug, label: z.displayName }))]}
        />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
          <Input label="Utilisations max" type="number" min={1} value={maxUses} onChange={(e) => setMaxUses(Math.max(1, parseInt(e.target.value) || 1))} />
          <Input label="Valable (heures)" type="number" min={1} value={ttlHours} onChange={(e) => setTtlHours(Math.max(1, parseInt(e.target.value) || 1))} />
        </div>
        {error && <div className="pl-alert" role="alert">{error}</div>}
        <Button variant="primary" onClick={() => { void handleCreate(); }} disabled={creating} style={{ alignSelf: 'flex-end' }}>
          {creating ? 'Création…' : 'Créer le lien'}
        </Button>
      </section>

      <section className="pl-panel" aria-label="Liens actifs" style={{ overflow: 'hidden' }}>
        <header style={{ padding: '16px 20px', borderBottom: '1px solid var(--color-border-default)' }}>
          <h2 className="pl-card-title">
            Liens actifs <span style={{ font: '700 11px/1 var(--font-mono)', color: 'var(--color-text-muted)' }}>{loading ? '…' : links.length}</span>
          </h2>
        </header>
        {!loading && links.length === 0 && (
          <p className="pl-page-sub" style={{ padding: '20px', color: 'var(--color-text-muted)' }}>Aucun lien actif. Crée-en un pour faire entrer quelqu'un sans compte.</p>
        )}
        <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
          {links.map((link, i) => (
            <li key={link.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px', borderTop: i ? '1px solid var(--color-border-subtle)' : 0 }}>
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ font: '700 12px/1.4 var(--font-mono)', overflowWrap: 'anywhere' }}>{shortUrl(link)}</span>
                <span className="pl-muted pl-num" style={{ fontSize: 12 }}>
                  {zoneName(link.zoneSlug)} · {link.usedCount} / {link.maxUses} utilisations · expire le {formatDate(link.expiresAt)}
                </span>
              </div>
              <Button variant="secondary" size="sm" icon={copied === link.id ? 'check' : 'link'} onClick={() => copy(link)}>
                {copied === link.id ? 'Copié' : 'Copier le lien'}
              </Button>
              <Button variant="ghost" size="sm" icon="x" aria-label="Désactiver ce lien" onClick={() => { void handleRevoke(link); }} />
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
